import { Fragment, ReactElement, memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useIsMobile from '../../../hooks/use-is-mobile';
import translation from '../../../languages';
import useRemoveFromCart from '../../../services/cart/use-remove-from-cart';
import enhancedEcommerce from '../../../utils/enhanced-ecommerce';
import schoolAccount from '../../../utils/school-account';
import CartItemBundles from './components/cart-item-bundles/cart-item-bundles';
import CartItem from './components/cart-item/cart-item';
import useBundleUpselling from './hooks/use-bundle-upselling';
import moveItemToFavorites from './utils/move-item-to-favorites';
import processCartItems from './utils/process-cart-items';
import removeItemFromCart from './utils/remove-item-from-cart';
import './cart-items.scss';

type CartItemsProps = TranslationProps & {
    cart: Cart;
    changeFavorite: (material: Material, add: boolean) => void;
};

const CartItems = memo(({ cart, changeFavorite, t }: CartItemsProps): ReactElement => {
    const { itemsInBundles, cartItems } = cart;

    useEffect((): void => enhancedEcommerce.tracker.checkoutStep1(cartItems), []);

    const user = useSelector((state: RootState) => state.main.user);
    const isSchoolAdmin = schoolAccount.isAdmin(user);
    const { mutate: removeFromCart } = useRemoveFromCart();

    const bundleUpselling = useBundleUpselling({ cartItems, isEnabled: !useIsMobile() });
    const processedCartItems = processCartItems(cartItems);

    const handleRemoveItemFromCart = (item: CartItem, index: number): void =>
        removeItemFromCart({ item, itemIndex: index, removeFromCart });
    const handleMoveItemToFavorites = (item: CartItem, index: number): void =>
        moveItemToFavorites({ item, itemIndex: index, changeFavorite, removeFromCart });

    return (
        <div className='cart-items-wrapper'>
            <div className='cart-items'>
                {processedCartItems.map((item, index): ReactElement => {
                    const materialId = item.material.id;
                    const isRestrictedForSchool = isSchoolAdmin && item.material.hasFixedPrice;

                    const isMaterialSelectedToShowBundlesFor = materialId === bundleUpselling.materialId;
                    const isAlreadyContainedInAnotherBundle = (itemsInBundles as number[]).includes(materialId);
                    const shouldShowBundles = isMaterialSelectedToShowBundlesFor && !isAlreadyContainedInAnotherBundle;

                    return (
                        <Fragment key={materialId}>
                            <CartItem
                                t={t}
                                index={index}
                                internalPath='cart_ci'
                                item={item}
                                onAddToFavorite={handleMoveItemToFavorites}
                                onRemove={handleRemoveItemFromCart}
                                className='mb-0'
                                alertMessage={
                                    isRestrictedForSchool
                                        ? t('common.materialBlock.restrictedMaterialForSchool')
                                        : undefined
                                }
                            />

                            {shouldShowBundles && <CartItemBundles bundles={bundleUpselling.bundles} />}
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
});

const translate = translation('cart');

export default translate(CartItems);
